<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { ref, watch } from 'vue'
import { Loader } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import Button from '../ui/button/Button.vue'
import { PlusIcon, UpdateIcon } from '@radix-icons/vue'
import Add from './sms/modals/Add.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import httpClient from '@/utils/httpClient'
import { useUserStore } from '@/stores/user'
import { Permission } from '@/lib/permissions/v1'
import { SMSGlobalRouteName } from '@/modules/sms-global/router'
import { smsGlobalsSchema } from '@/lib/schema/v1/sms-global'
import { SMSGlobal } from '@/lib/types/v1'

const props = defineProps<{
    active: boolean
}>()

const user = useUserStore()

const loading = ref(false)
const servers = ref<SMSGlobal[]>([])
const openAddModal = ref(false)
const selectedServer = ref(null)

const init = async () => {
    try {
        loading.value = true

        const { data } = await httpClient.get('v1/sms-global/servers')

        servers.value = smsGlobalsSchema.parse(data.data)
    } finally {
        loading.value = false
    }
}

const add = (server) => {
    selectedServer.value = server
    openAddModal.value = true
}

watch(
    () => props.active,
    async () => {
        if (props.active) {
            await init()
        }
    }
)
</script>

<template>
    <AccordionItem value="smsGlobal" class="border-b-0">
        <AccordionTrigger
            class="group py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800 justify-between"
        >
            <div class="flex items-center justify-between flex-1">
                <div class="flex items-center">
                    <div class="w-6">
                        <FontAwesomeIcon :icon="['fal', 'comment-sms']" class="size-4!" />
                    </div>
                    <div class="ml-3 text-xs">SMS Global</div>
                </div>
            </div>
            <div class="flex items-center">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button
                                v-if="user.hasPermission(Permission.SMSGlobalServerCreate)"
                                variant="icon"
                                size="icon-xs"
                                class="h-4 w-4 mr-2 hidden group-hover:flex"
                                @click.stop="add"
                            >
                                <PlusIcon class="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Add SMS Global server</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button
                                variant="icon"
                                size="icon-xs"
                                class="h-4 w-4 mr-2 hidden group-hover:flex"
                                @click.stop="init"
                            >
                                <UpdateIcon class="h-3 w-3" :class="{ 'animate-spin': loading }" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Refresh</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs text-white">
            <div v-if="loading" class="flex items-center space-x-2 pl-[52px] py-2">
                <Loader class="mr-2 h-4 w-4 shrink-0 opacity-50 animate-spin" />
                <span>Loading...</span>
            </div>
            <div v-else class="flex flex-col">
                <template v-for="server in servers" :key="server?.uuid">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <RouterLink
                                    :to="{
                                        name: SMSGlobalRouteName.ServerSMSIndex,
                                        params: {
                                            serverId: server?.uuid,
                                        },
                                    }"
                                    class="pl-[52px] py-2 pr-4 hover:bg-neutral-600/50 truncate"
                                    >{{ server?.title }}</RouterLink
                                >
                            </TooltipTrigger>
                            <TooltipContent side="right">
                                <p>{{ server?.title }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </template>
            </div>
        </AccordionContent>
        <Add v-model="openAddModal" @success="init" />
    </AccordionItem>
</template>

<style scoped>
.router-link-exact-active {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
